import React, { useState, useEffect } from "react";
import {
  Heart,
  Gift,
  Star,
  Users,
  Clock,
  MessageSquare,
  Zap,
  Crown,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { BookDemoModal } from "../components/BookDemoModal";

export default function PilotPage() {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <div className="font-sans min-h-screen" style={{ backgroundColor: "#0D2B22", color: "#FFFFFF" }}>
      <Navbar />

      <main className="pt-0 pb-12 md:py-16">
        {/* Hero Section */}
        <section className="pt-8 pb-16 md:py-20 relative overflow-hidden" style={{ backgroundColor: "#0D2B22" }}>
          {/* Exclusive badge element */}
          <div className="absolute top-8 right-8 sm:top-12 sm:right-12 z-20">
            <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#2EB39A] to-emerald-600 rounded-full shadow-lg">
              <Crown className="w-4 h-4 text-white" />
              <span className="text-xs font-bold text-white uppercase tracking-wider">Exclusive Invitation</span>
            </div>
          </div>

          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <div className="inline-flex items-center px-4 py-2 rounded-full border mb-6" style={{ backgroundColor: "#0F3328", borderColor: "#1A4035" }}>
              <span className="text-sm font-semibold uppercase tracking-wide" style={{ color: "#2EB39A" }}>
                Hands-On Pilot Program
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-4 sm:mb-6 px-2">
              Be a{" "}
              <span className="bg-gradient-to-r from-[#2EB39A] to-emerald-400 bg-clip-text text-transparent">
                Founding Partner
              </span>
              <br className="hidden sm:block" />
              <span style={{ color: "#9ABFB5" }}> (hands-on pilot)</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl mb-6 sm:mb-8 max-w-3xl mx-auto leading-relaxed font-medium px-2" style={{ color: "#9ABFB5" }}>
              Join a small group shaping the product with us. We'll work on real (or redacted) docs, prioritise your biggest paper-cuts, and ship improvements quickly.
            </p>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left Side - Commitment & Benefits */}
            <div className="space-y-8">
              {/* Commitment Section */}
              <div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white leading-tight mb-4 sm:mb-6">
                  <span className="bg-gradient-to-r from-[#2EB39A] to-emerald-400 bg-clip-text text-transparent">
                    Commitment
                  </span>
                  <span className="text-lg sm:text-xl font-normal ml-2" style={{ color: "#9ABFB5" }}>(light but real)</span>
                </h2>
                <p className="text-base sm:text-lg leading-relaxed mb-6 sm:mb-8" style={{ color: "#9ABFB5" }}>
                  We value your time and want to make this partnership meaningful for both sides.
                </p>
              </div>

              <div className="space-y-6">
                {/* Time Commitment */}
                <div className="group flex items-start space-x-6 p-6 rounded-[32px] border transition-all duration-300" style={{ backgroundColor: "#0F3328", borderColor: "#1A4035" }}>
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl flex items-center justify-center flex-shrink-0 relative" style={{ backgroundColor: "#1A4035" }}>
                    <Clock className="w-8 h-8 sm:w-10 sm:h-10" style={{ color: "#2EB39A" }} />
                    <div className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-[#2EB39A] rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-[#0D2B22]">✓</span>
                    </div>
                  </div>
                  <div className="pt-2">
                    <h3 className="text-xl font-bold text-white mb-2">
                      15-30 min check-in every 2 weeks
                    </h3>
                    <p className="text-sm" style={{ color: "#9ABFB5" }}>
                      For 8-10 weeks. Quick syncs to keep momentum without overwhelming your schedule.
                    </p>
                  </div>
                </div>

                {/* Feedback Commitment */}
                <div className="group flex items-start space-x-6 p-6 rounded-[32px] border transition-all duration-300" style={{ backgroundColor: "#0F3328", borderColor: "#1A4035" }}>
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl flex items-center justify-center flex-shrink-0 relative" style={{ backgroundColor: "#1A4035" }}>
                    <MessageSquare className="w-8 h-8 sm:w-10 sm:h-10" style={{ color: "#2EB39A" }} />
                    <div className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-[#2EB39A] rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-[#0D2B22]">✓</span>
                    </div>
                  </div>
                  <div className="pt-2">
                    <h3 className="text-xl font-bold text-white mb-2">
                      Fast feedback & micro-surveys
                    </h3>
                    <p className="text-sm" style={{ color: "#9ABFB5" }}>
                      Screenshots/Looms and 2-3 micro-surveys per month. Quick, painless input.
                    </p>
                  </div>
                </div>

                {/* Direct Access */}
                <div className="group flex items-start space-x-6 p-6 rounded-[32px] border transition-all duration-300" style={{ backgroundColor: "#0F3328", borderColor: "#1A4035" }}>
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl flex items-center justify-center flex-shrink-0 relative" style={{ backgroundColor: "#1A4035" }}>
                    <Zap className="w-8 h-8 sm:w-10 sm:h-10" style={{ color: "#2EB39A" }} />
                    <div className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-[#2EB39A] rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-[#0D2B22]">✓</span>
                    </div>
                  </div>
                  <div className="pt-2">
                    <h3 className="text-xl font-bold text-white mb-2">
                      Direct line to the team
                    </h3>
                    <p className="text-sm" style={{ color: "#9ABFB5" }}>
                      Rapid iteration with direct access. Your feedback shapes the product in real-time.
                    </p>
                  </div>
                </div>
              </div>

              {/* What You Get Section */}
              <div className="mt-12">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white leading-tight mb-4 sm:mb-6">
                  <span className="bg-gradient-to-r from-[#2EB39A] to-emerald-400 bg-clip-text text-transparent">
                    What You Get
                  </span>
                </h2>
              </div>

              <div className="space-y-6">
                {/* Early Access */}
                <div className="group flex items-start space-x-6 p-6 rounded-[32px] border transition-all duration-300" style={{ backgroundColor: "#0F3328", borderColor: "#1A4035" }}>
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl flex items-center justify-center flex-shrink-0 relative" style={{ backgroundColor: "#1A4035" }}>
                    <Heart className="w-8 h-8 sm:w-10 sm:h-10" style={{ color: "#2EB39A" }} />
                    <div className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-[#2EB39A] rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-[#0D2B22]">✓</span>
                    </div>
                  </div>
                  <div className="pt-2">
                    <h3 className="text-xl font-bold text-white mb-2">
                      Early access with white-glove onboarding
                    </h3>
                    <p className="text-sm" style={{ color: "#9ABFB5" }}>
                      Premium support to get you up and running quickly.
                    </p>
                  </div>
                </div>

                {/* Influence */}
                <div className="group flex items-start space-x-6 p-6 rounded-[32px] border transition-all duration-300" style={{ backgroundColor: "#0F3328", borderColor: "#1A4035" }}>
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl flex items-center justify-center flex-shrink-0 relative" style={{ backgroundColor: "#1A4035" }}>
                    <Users className="w-8 h-8 sm:w-10 sm:h-10" style={{ color: "#2EB39A" }} />
                    <div className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-[#2EB39A] rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-[#0D2B22]">✓</span>
                    </div>
                  </div>
                  <div className="pt-2">
                    <h3 className="text-xl font-bold text-white mb-2">
                      Influence on the roadmap and sequencing
                    </h3>
                    <p className="text-sm" style={{ color: "#9ABFB5" }}>
                      Your priorities become our priorities. Help shape what gets built next.
                    </p>
                  </div>
                </div>

                {/* Recognition */}
                <div className="group flex items-start space-x-6 p-6 rounded-[32px] border transition-all duration-300" style={{ backgroundColor: "#0F3328", borderColor: "#1A4035" }}>
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl flex items-center justify-center flex-shrink-0 relative" style={{ backgroundColor: "#1A4035" }}>
                    <Crown className="w-8 h-8 sm:w-10 sm:h-10" style={{ color: "#2EB39A" }} />
                    <div className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-[#2EB39A] rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-[#0D2B22]">✓</span>
                    </div>
                  </div>
                  <div className="pt-2">
                    <h3 className="text-xl font-bold text-white mb-2">
                      Founder recognition & preferential pricing
                    </h3>
                    <p className="text-sm" style={{ color: "#9ABFB5" }}>
                      Opt-in recognition on our "Partner Wall" and preferential launch pricing.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - CTA Card */}
            <div className="rounded-[32px] border-2 shadow-xl p-6 sm:p-8 md:p-10 relative" style={{ backgroundColor: "#0F3328", borderColor: "#1A4035" }}>
              {/* Limited spots badge */}
              <div className="absolute -top-4 right-6">
                <div className="px-4 py-2 bg-gradient-to-r from-[#2EB39A] to-emerald-600 rounded-full shadow-lg">
                  <span className="text-xs font-bold text-white uppercase tracking-wider">Limited Spots</span>
                </div>
              </div>

              <div className="mb-6 sm:mb-8">
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
                  Apply for the Pilot
                </h3>
                <p className="text-sm sm:text-base mb-2" style={{ color: "#9ABFB5" }}>
                  Spots are limited so we can stay hands-on. Book a demo and we'll get back to you within 24 hours.
                </p>
                <p className="text-xs font-semibold" style={{ color: "#2EB39A" }}>
                  Limited availability • First come, first served
                </p>
              </div>

              <div className="space-y-6">
                <button
                  type="button"
                  onClick={() => setDemoOpen(true)}
                  className="w-full rounded-xl bg-[#2EB39A] px-8 py-3 font-bold text-[#0D2B22] hover:bg-[#25927E] transition-colors text-base sm:text-lg"
                >
                  Book a Demo
                </button>

                <p className="text-sm text-center" style={{ color: "#9ABFB5" }}>
                  By booking a demo, you agree to our terms of service and privacy policy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      <BookDemoModal isOpen={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}
